import React, { useEffect, useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TextField,
  TableHead,
  TableRow,
  Paper,
  Pagination,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  FormControlLabel,
  Checkbox,
  Button,
} from '@mui/material';
import {
  fetchPatients,
  setPatientsPage,
  setPatientsPageSize,
  fetchSearchedPatients,
} from '../../../actions/PatientActions';
import PageHeader from '../../../components/PageHeader';
import { debounce, noConflict } from 'lodash';
import { hasPermission } from '../../../utils/common';

import {
  permissionsList,
  permissionsWithAssignedIds,
} from '../../../api/constant';
import { toast } from 'sonner';
import { deletePatientApi } from '../../../api/patientApi';
import { useNavigate } from 'react-router-dom';
import LoadingOverlay from '../../../components/LoadingOverlay';
import UIModal from '../../../components/UIElements/UIModal';
import PatientTable from '../../../common/PatientTable';
import ProtectedComponent from '../../../common/ProtectedComponent';
export default function ViewAllPatients() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { patients, totalPatients, page, pageSize, loading, error } =
    useSelector((state) => state.patientReducer);
  const permissionMap = useSelector(
    (state) => state.loginReducer?.permissionsMap,
  );
  const [searchValue, setSearchValue] = useState('');
  const [initialRender, setInitialRender] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);
  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [patientIdToDelete, setPatientIdToDelete] = useState(null);
  const [searchKey, setSearchKey] = useState('firstName');
  useEffect(() => {
    toast.promise(dispatch(fetchPatients(page, pageSize)), {
      loading: 'Loading patients...',
      success: 'Patients Loaded',
      error: 'Failed to load patients',
      duration: 100,
    });
  }, [dispatch, page, pageSize]);

  const handlePageChange = (event, value) => {
    dispatch(setPatientsPage(value));
  };

  const handlePageSizeChange = (event) => {
    dispatch(setPatientsPageSize(event.target.value));
  };

  const searchHandler = useCallback(
    debounce((searchValue) => {
      if (searchValue) {
        toast.promise(
          dispatch(
            fetchSearchedPatients(
              1,
              pageSize,
              searchValue,
              searchKey === 'pid',
              searchKey === 'aadhar',
            ),
          ),
          {
            loading: 'Searching patients...',
            success: 'Patients Loaded',
            error: 'Failed to load patients',
          },
        );
      } else {
        toast.promise(dispatch(fetchPatients(1, pageSize)), {
          loading: 'Loading patients...',
          success: 'Patients Loaded',
          error: 'Failed to load patients',
          duration: 100,
        });
      }
    }, 500), // Adjust the debounce delay as necessary
    [dispatch, pageSize, searchKey],
  );

  useEffect(() => {
    if (!initialRender || searchValue) {
      searchHandler(searchValue);
    }
  }, [searchValue]);

  useEffect(() => {
    if (initialRender) {
      setInitialRender(false);
      // toast.promise(dispatch(fetchPatients(1, pageSize)), {
      //   loading: 'Loading patients...',
      //   success: 'Patients Loaded',
      //   error: 'Failed to load patients',
      //   duration: 100,
      // });
    }
  }, []);

  const handleSearchChange = (event) => {
    if (searchKey == 'pid') {
      setSearchValue(event.target.value.toUpperCase().trim());
    } else {
      setSearchValue(event.target.value.trim());
    }
  };

  const handleEditClick = (id) => {
    navigate(`/patient/${id}/edit`);
  };
  const handleDeleteClick = (id) => {
    setPatientIdToDelete(id);
    setOpenConfirmModal(true);
  };

  const handleConfirmDelete = async () => {
    setIsDeleting(true);
    try {
      await deletePatientApi(patientIdToDelete);
      toast.success('Patient deleted successfully');
      dispatch(fetchPatients(page, pageSize));
    } catch (error) {
      toast.error('Failed to delete patient');
    } finally {
      setIsDeleting(false);
      setOpenConfirmModal(false);
      setPatientIdToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setOpenConfirmModal(false);
    setPatientIdToDelete(null);
  };

  return (
    <ProtectedComponent
      requiredPermission={
        permissionsWithAssignedIds[permissionsList.ViewPatient]
      }
    >
      <div className="theme-blush">
        <div className="flex" style={{ alignItems: 'flex-start' }}>
          <div className="container-fluid" style={{ flex: '1 0 0' }}>
            <PageHeader
              HeaderText="View Patients"
              Breadcrumb={[
                { name: 'Patient', navigate: '/view-patients' },
                { name: 'View Patients', navigate: '/view-patients' },
              ]}
            />
          </div>
          <button
            onClick={() => navigate('/patient-form')}
            className="btn btn-primary"
            style={{ marginTop: '20px', marginRight: '20px' }}
          >
            Add Patient
          </button>
        </div>
      </div>
      <div className="component-wrapper">
        <div
          style={{
            display: 'flex',
            marginBottom: '10px',
            width: '30vw',
            border: '1px solid grey',
            borderRadius: '12px',
            justifyContent: 'center',
            alignItems: 'center',
            margin: '0 auto',
          }}
        >
          <select
            style={{
              border: 'none',
              borderRight: '1px solid grey',
              borderRadius: '0',
              borderTopLeftRadius: '12px',
              borderBottomLeftRadius: '12px',
            }}
            className="col-md-5 custom-select"
            value={searchKey}
            onChange={(e) => {
              setSearchKey(e.target.value);
            }}
            required
          >
            <option value="firstName">Name</option>
            <option value="pid">Patient ID</option>
            <option value="aadhar">Aadhar Number</option>
          </select>

          <input
            style={{
              border: 'none',
              borderRadius: '0',
              borderTopRightRadius: '12px',
              borderBottomRightRadius: '12px',
            }}
            aria-label="Text input with dropdown button"
            className="col-md-7 form-control"
            type="text"
            placeholder={`Search by ${searchKey == 'pid' ? 'patient id' : searchKey == 'firstName' ? 'first name' : 'aadhar number'}`}
            onChange={handleSearchChange}
          />
        </div>

        {hasPermission(
          permissionMap,
          permissionsWithAssignedIds[permissionsList.ViewPatient],
        ) ? (
          <>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Patient ID</TableCell>
                    <TableCell>Aadhaar Number</TableCell>
                    <TableCell>First Name</TableCell>
                    <TableCell>Last Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Phone Number</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {patients.map((patient, index) => (
                    <TableRow key={index}>
                      <TableCell>{patient.id}</TableCell>
                      <TableCell>
                        {patient.externalPatientId ?? patient.upid}
                      </TableCell>
                      <TableCell>{patient.adharNumber}</TableCell>
                      <TableCell>{patient.firstName}</TableCell>
                      <TableCell>{patient.lastName}</TableCell>
                      <TableCell>{patient.email}</TableCell>
                      <TableCell>+{patient.phone}</TableCell>
                      <TableCell style={{ padding: '5px' }}>
                        <IconButton
                          onClick={(event) => {
                            event.stopPropagation();
                            navigate(`/patient/${patient.id}/edit`);
                          }}
                        >
                          <i className="fa fa-pencil"></i>
                        </IconButton>
                        <IconButton
                          disabled
                          onClick={() => handleDeleteClick(patient.id)}
                        >
                          <i className="fa fa-trash-o"></i>
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {isDeleting && <LoadingOverlay message="Deleting patient..." />}

            <UIModal
              title="Please confirm?"
              body="Once deleted, this cannot be undone"
              proceedText="Confirm"
              cancelText="Cancel"
              show={openConfirmModal}
              onClose={handleCancelDelete}
              onSave={handleConfirmDelete}
            />
          </>
        ) : (
          <>
            <PatientTable
              patients={patients}
              onEditClick={handleEditClick}
              onDeleteClick={handleDeleteClick}
            />

            {isDeleting && <LoadingOverlay message="Deleting patient..." />}

            <UIModal
              title="Please confirm?"
              body="Once deleted, this cannot be undone"
              proceedText="Confirm"
              cancelText="Cancel"
              show={openConfirmModal}
              onClose={handleCancelDelete}
              onSave={handleConfirmDelete}
            />
          </>
        )}

        <Pagination
          count={Math.ceil(totalPatients / pageSize)}
          page={page}
          onChange={handlePageChange}
          color="primary"
          style={{
            marginTop: '20px',
            display: 'flex',
            justifyContent: 'center',
          }}
        />

        <FormControl
          variant="outlined"
          style={{ minWidth: 120, marginBottom: '20px' }}
        >
          <InputLabel id="page-size-label">Page Size</InputLabel>
          <Select
            labelId="page-size-label"
            id="page-size-select"
            value={pageSize}
            onChange={handlePageSizeChange}
            label="Page Size"
          >
            <MenuItem value={5}>5</MenuItem>
            <MenuItem value={10}>10</MenuItem>
            <MenuItem value={15}>15</MenuItem>
            <MenuItem value={20}>20</MenuItem>
          </Select>
        </FormControl>
      </div>
    </ProtectedComponent>
  );
}
